<script setup>
    // Libraries
    import { storeToRefs } from 'pinia';
    import { useRouteQuery } from '@vueuse/router'
    import { watchDebounced } from '@vueuse/core';
    import { useSitesStore } from '@/stores/sites';
    
    // Stores
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';
    import { useUserStore } from '@/stores/user';
    import { useLocationStore } from '@/stores/location';

    // Variables
    const { getSiteNeighborhoodName } = useUtils();    
    const userStore = useUserStore();
    const { sites: userSites } = storeToRefs(userStore);
    const neighborhoodsStore = useNeighborhoodsStore();
    const neighborhoodsMap = neighborhoodsStore.neighborhoodsMap;
    
    // Sites Store
    const { sitesData } = storeToRefs(useSitesStore());
    const locationStore = useLocationStore();

    // Filters
    const nearMe = useRouteQuery('nearMe', false);
    const sortBy = useRouteQuery('sortBy', 'alpha');
    const mapView = useRouteQuery('mapView', false );
    // Mount the map (and vue3-google-map with it) the first time map view is
    // shown, then keep it mounted so toggling back preserves its state. With
    // v-show alone it mounted in list view too, which most visitors never leave.
    const mapShown = ref(!!mapView.value);
    watch(mapView, (v) => { if (v) mapShown.value = true; });
    const siteTypeFilters = useRouteQuery('siteTypeFilters', []);
    const amenitiesFilters = useRouteQuery('amenitiesFilters', []);
    const datesFilters = useRouteQuery('datesFilters', '');
    const searchTerm = ref('');

    // Properties
    const showFilters = ref(false);
    const loading = ref(false);

    // Filters/Sorts
    const showSearchLabel = ref(true);
    const sitesSource = ref([...sitesData.value]);

    // `nearMe` arrives as the string 'true' from the URL but as a real boolean
    // when the filter panel writes it, and `true.length` is undefined — which is
    // how the near-me filter below silently stopped filtering. Normalise once.
    const nearMeActive = computed(() => nearMe.value === true || nearMe.value === 'true');

    // Near Me implies proximity order unless the visitor explicitly picked a sort.
    // The dropdown always writes ?sortBy=, so its absence means "not chosen" —
    // which is the case when you land straight on ?nearMe=true, exactly how O2-41
    // was reported. Applying Near Me from the filter panel now sets the dropdown
    // to Distance as well, so the two paths agree.
    const route = useRoute();
    const effectiveSort = computed(() => (
        nearMeActive.value && !('sortBy' in route.query) ? 'distance' : sortBy.value
    ));


    // The layout renders <GlobalLocation> only when this flag is true, and
    // GlobalLocation is the only thing that ever requests a position — so anything
    // needing distances has to raise the flag first.
    //
    // This was a bare `if` in setup, evaluated once against the initial route
    // query, which left the flag correct only because FiltersSites.applyFilters()
    // happens to set it too. Any route-query change that doesn't remount the page
    // (browser back/forward onto ?sortBy=distance, a link into a filtered view)
    // slipped through both. Watching removes the duplication and covers every
    // entry point.
    watch(
        [nearMeActive, effectiveSort],
        () => {
            if (nearMeActive.value || effectiveSort.value === 'distance') userStore.setHasLocationPermission(true);
        },
        { immediate: true },
    );

    // Drives the map's framing: a filtered listing frames its own results, an
    // unfiltered one keeps the downtown default (Asana O2-45).
    const hasActiveFilters = computed(() => Boolean(
        nearMeActive.value
        || searchTerm.value
        || coerceArray(siteTypeFilters.value).length
        || coerceArray(amenitiesFilters.value).length
        || (datesFilters.value && datesFilters.value !== 'all'),
    ));

    // The pipeline lives in app/utils/siteFiltering.ts so Add Sites runs exactly
    // the same rules (Asana O2-54). Each of them was its own ticket — "Both"
    // meaning open BOTH days, isClosedHours rather than a string compare, Near
    // Me testing distance as a number so standing on a site does not drop it,
    // and the Sortkey/"The" handling in the alpha sort.
    const filteredSites = computed(() => filterAndSortSites({
        sites: sitesSource.value,
        searchTerm: searchTerm.value,
        getNeighborhoodName: getSiteNeighborhoodName,
        neighborhoodsMap,
        siteTypeFilters: siteTypeFilters.value,
        amenitiesFilters: amenitiesFilters.value,
        datesFilters: datesFilters.value,
        nearMe: nearMe.value,
        sortBy: effectiveSort.value,
        coords: locationStore.coords,
        calculateDistance: (lat, lng) => locationStore.calculateDistance(lat, lng),
        userSites: userSites?.value ?? [],
    }));



    function toggleFilters() {
        searchTerm.value = '';
        showFilters.value = !showFilters.value;
    }

    // Count badge on the filter button, so an applied filter is visible
    // without opening the panel.
    const activeFilterCount = computed(() => countActiveSiteFilters({
        nearMe: nearMe.value,
        siteTypeFilters: siteTypeFilters.value,
        amenitiesFilters: amenitiesFilters.value,
        datesFilters: datesFilters.value,
    }));
    const filtersButtonLabel = computed(() => (
        activeFilterCount.value ? `Filters, ${activeFilterCount.value} applied` : 'Filters'
    ));

    function clearFilters() {
        searchTerm.value = '';
        nearMe.value = false;
        siteTypeFilters.value = [];
        amenitiesFilters.value = [];
        datesFilters.value = '';
    }

    // Result count for screen readers. Filtering and search change the list
    // silently otherwise. Debounced so typing in search announces once, and
    // only set on change so nothing is read out on page load.
    const resultsStatus = ref('');
    watchDebounced(
        () => filteredSites.value.length,
        (count) => {
            resultsStatus.value = count === 0
                ? 'No sites match these filters.'
                : `${count} ${count === 1 ? 'site' : 'sites'} found`;
        },
        { debounce: 600 },
    );

    const searchInputId = useId();

    function viewToggle(n) {
        mapView.value = n;
    }

    function updateSites(val) {
        console.info('Updating sites in page component', val.length);
        showFilters.value = false;
        if (loading.value === true) loading.value = false;
        console.info('Updating sites in page component', val.length);        
    }

    function resetSites() {
        console.info('Resetting sites in page component');
        sitesSource.value = sitesData.value;
    }

    function setMapView(val) {
        mapView.value = val;
    }

    watch(sitesData, () => {
        sitesSource.value = sitesData.value;
    });

</script>

<template>
    <section class="sites-overview sites-page" :class="{ 'is-map-view': mapView }" v-if="!loading">
        <template v-if="sitesData && sitesData.length > 0">
            <GlobalFlyout v-show="showFilters" @toggleFilters="toggleFilters" :showing="showFilters">
                <FiltersSites
                    :mapView="mapView"
                    :sites="sitesData"
                    :ohcFavoriteSites="userSites"
                    @updateSites="updateSites"
                    @setMapView="setMapView"
                    @close="toggleFilters"
                    :coords="locationStore.coords"
                    @resetSites="resetSites" />
            </GlobalFlyout>

            <div id="content-wrapper">
                <BlocksTitleMapListToggle @viewToggle="viewToggle" title="2026 Sites" :mapView="mapView">
                    <button type="button" class="show-filters" @click="toggleFilters" ref="filterToggle" :aria-label="filtersButtonLabel" :aria-expanded="String(showFilters)">
                        <SvgsFilters class="filter-icon" />
                        <span v-if="activeFilterCount" class="filter-count" aria-hidden="true">{{ activeFilterCount }}</span>
                    </button>
                    <button type="button" class="desktop-filters button black" @click="toggleFilters" ref="filterToggle" :aria-expanded="String(showFilters)">
                        Filters<span v-if="activeFilterCount"> ({{ activeFilterCount }})</span>
                    </button>                    
                </BlocksTitleMapListToggle>
                <div class="container hd-container" v-show="!showFilters && !mapView">
                    <div class="filters">
                        <div class="form-field search" role="search">
                            <!-- "Search sites", not "Search": the header now opens a
                                 universal search across the whole site, so this one
                                 needs to say what it covers (Asana O2-15). -->
                            <label :for="searchInputId" :class="{ 'sr-only': !showSearchLabel || searchTerm !== '' }">Search sites</label>
                            <SvgsSearch />
                            <input :id="searchInputId" type="search" v-model="searchTerm" />
                        </div>
                    </div>
                </div>
                <BlocksMap v-if="mapShown" v-show="mapView" :locations="filteredSites" :offset="20" zoom-control
                    :show-user-location="!!mapView" zoom-default :frame-to-locations="hasActiveFilters" />
                <div class="hd-container">
                    <BlocksSitesListStack v-show="!mapView && !showFilters" :showHeader="false" :cards="filteredSites" />
                </div>
                <!-- Always mounted: a live region only announces text that lands
                     in a region already in the DOM. -->
                <p class="sr-only" role="status">{{ resultsStatus }}</p>
                <div class="container hd-container" v-if="filteredSites.length === 0">
                    <template v-if="!mapView">
                        <p class="no-results subhead-2">No sites match these filters.</p>
                        <button v-if="hasActiveFilters" type="button" class="clear-filters button black" @click="clearFilters">Clear filters</button>
                    </template>
                    <template v-else>
                        <BlocksTurquoiseNoResults />
                    </template>
                </div>
            </div>
        </template>
    </section>
</template>

<style lang="scss">
    .sites-overview {
        position: relative;

        .is-iframe & {
            margin-bottom: 32px;
        }

        // The grid is the last thing on the page, so without this the final row
        // sits flush against the footer. 36px — double the 18px `.filters`
        // leaves above the grid, which read too tight against the footer's
        // solid red band.
        //
        // Qualified with .sites-page, a class that exists only here. This style
        // block is NOT scoped, and .sites-overview is also used by /stops with
        // the same list stack inside it — so a bare `.sites-overview` rule
        // reached that page too, but only once this component had been loaded.
        // /stops would then be spaced differently depending on whether you had
        // visited /sites first in the same session.
        //
        // On the stack itself rather than its .hd-container wrapper, because the
        // stack is v-show'd — in map view it goes display:none and takes the
        // margin with it, instead of leaving a gap under the map.
        &.sites-page .sites-list-stack {
            margin-bottom: 36px;
        }

        .filters {
            display: flex;
            margin-bottom: 18px;

            @media (min-width: $tablet) {
                width: 100%;
            }

            .filter-icon {
                flex-shrink: 0;
                margin-right: 16px;
            }

            .select {
                label {
                    display: block;
                    margin-bottom: 16px;
                }
            }
        }

        // Qualified with .sites-page: this block is unscoped and /stops reuses
        // .sites-overview (see the margin note above).
        &.sites-page .show-filters {
            position: relative;
        }

        &.sites-page .filter-count {
            position: absolute;
            top: -6px;
            right: 8px;
            min-width: 18px;
            height: 18px;
            padding: 0 4px;
            border-radius: 9px;
            background: $black;
            color: $white;
            font-size: 11px;
            font-weight: 800;
            line-height: 18px;
            text-align: center;
        }

        &.sites-page .clear-filters {
            margin-top: 16px;
            margin-bottom: 36px;
        }

        .map-sites-carousel {
            .component-header {
                display: none;
            }

            .carousel-component .carousel-inner {
                padding-top: 4px;
                padding-bottom: 4px;
            }
        }

        .desktop-filters {
            display: none;
        }

        .title-map-list-toggle.component-header {
            padding: 20px 16px;

            // $hd + the two gutters, matching .hd-container: at exactly $hd the
            // page cap leaves no margin of its own, so dropping the padding
            // there put the title 5px from the window edge.
            @media (min-width: $hd + 32px) {
                padding: 20px 0px;
            }
        }

        // In map view this row is the last thing above the map, and the map's
        // height is measured in JS from its bottom edge — so its vertical
        // padding comes straight out of the map. List view keeps the roomier
        // spacing. Keyed off the view flag rather than `:has()` — the map
        // container is in the DOM in both views, just hidden
        // (Asana 1217333501985926).
        &.is-map-view .title-map-list-toggle.component-header {
            padding-top: 12px;
            padding-bottom: 12px;
        }

        .carousel-component .carousel-inner .carousel-item.is-full-width {
            align-self: flex-end;
        }

        @media (min-width: $tablet) {
            max-width: $hd;
            width: 100vw;
            margin: auto;
            min-height: 60vh;

            .carousel-component .carousel-inner .carousel-item.is-full-width {
                align-self: stretch;
            }

            .show-filters {
                display: none;
            }

            .desktop-filters {
                display: block;
                width: auto;
                padding: 10px 70px;
                margin-right: 16px;
            }
        }
    }
</style>
